// Age Category Utility Functions
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

// Module-level cache: survives component remounts and tab switches, cleared on page refresh
const _ageCategoryCache = new Map();

/**
 * Calculate age category from birth year only (simplified version)
 */
export function getAgeCategory(birthYear) {
  if (!birthYear || isNaN(birthYear)) return null;
  
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  
  if (age >= 7 && age <= 9) return 'Juvenile I';
  if (age >= 10 && age <= 11) return 'Juvenile II';
  if (age >= 12 && age <= 13) return 'Junior I';
  if (age >= 14 && age <= 15) return 'Junior II';
  if (age >= 16 && age <= 18) return 'Youth';
  if (age >= 19) return 'Adult';
  
  return null;
}

/**
 * Age category hierarchy for comparison (lower index = younger category)
 */
export const AGE_CATEGORY_HIERARCHY = ['Juvenile I', 'Juvenile II', 'Junior I', 'Junior II', 'Youth', 'Adult'];

/**
 * Compare two age categories
 * @param {string} category1 
 * @param {string} category2 
 * @returns {number} -1 if category1 < category2, 0 if equal, 1 if category1 > category2
 */
export function compareAgeCategories(category1, category2) {
  const index1 = AGE_CATEGORY_HIERARCHY.indexOf(category1);
  const index2 = AGE_CATEGORY_HIERARCHY.indexOf(category2);
  
  if (index1 === -1 || index2 === -1) return 0; // Unknown categories are equal
  
  return index1 < index2 ? -1 : (index1 > index2 ? 1 : 0);
}

/**
 * Calculate age category from birth date
 */
export function calculateAgeCategory(birthDate) {
  if (!birthDate) return null;
  
  try {
    let day, month, year;
    
    // Handle both dd/mm/yyyy and yyyy-mm-dd formats
    if (birthDate.includes('-')) {
      // yyyy-mm-dd format (from database)
      const parts = birthDate.split('-');
      if (parts.length !== 3) return null;
      [year, month, day] = parts.map(num => parseInt(num));
    } else if (birthDate.includes('/')) {
      // dd/mm/yyyy format (from form input)
      const parts = birthDate.split('/');
      if (parts.length !== 3) return null;
      [day, month, year] = parts.map(num => parseInt(num));
    } else {
      return null;
    }
    
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    
    const birthYear = year;
    const currentYear = new Date().getFullYear();
    
    // Calculate age based on year only (as of January 1st of current year)
    const age = currentYear - birthYear;
    
    if (age >= 7 && age <= 9) return 'Juvenile I';
    if (age >= 10 && age <= 11) return 'Juvenile II';
    if (age >= 12 && age <= 13) return 'Junior I';
    if (age >= 14 && age <= 15) return 'Junior II';
    if (age >= 16 && age <= 18) return 'Youth';
    if (age >= 19) return 'Adult';
    
    return null;
  } catch (error) {
    console.error('Error calculating age category:', error);
    return null;
  }
}

/**
 * Calculate age category from birth date for a specific year
 */
export function calculateAgeCategoryForYear(birthDate, competitionYear) {
  if (!birthDate || !competitionYear) return null;
  
  try {
    let day, month, year;
    
    // Handle both dd/mm/yyyy and yyyy-mm-dd formats
    if (birthDate.includes('-')) {
      // yyyy-mm-dd format (from database)
      const parts = birthDate.split('-');
      if (parts.length !== 3) return null;
      [year, month, day] = parts.map(num => parseInt(num));
    } else if (birthDate.includes('/')) {
      // dd/mm/yyyy format (from form input)
      const parts = birthDate.split('/');
      if (parts.length !== 3) return null;
      [day, month, year] = parts.map(num => parseInt(num));
    } else {
      return null;
    }
    
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    
    const birthYear = year;
    
    // Calculate age based on the specific year (as of January 1st of that year)
    const age = competitionYear - birthYear;
    
    if (age >= 7 && age <= 9) return 'Juvenile I';
    if (age >= 10 && age <= 11) return 'Juvenile II';
    if (age >= 12 && age <= 13) return 'Junior I';
    if (age >= 14 && age <= 15) return 'Junior II';
    if (age >= 16 && age <= 18) return 'Youth';
    if (age >= 19) return 'Adult';
    
    return null;
  } catch (error) {
    console.error('Error calculating age category for year:', error);
    return null;
  }
}

/**
 * Get eligible age categories for ranking calculations
 * Current category and all higher categories (exclude lower categories)
 */
export function getEligibleCategoriesForRankings(currentCategory) {
  if (!currentCategory) return [];
  
  const categoryHierarchy = [
    'Juvenile I',
    'Juvenile II', 
    'Junior I',
    'Junior II',
    'Youth',
    'Adult'
  ];
  
  const currentIndex = categoryHierarchy.indexOf(currentCategory);
  if (currentIndex === -1) return [currentCategory];
  
  // Include current category and all higher (older) categories
  const eligible = [];
  for (let i = currentIndex; i < categoryHierarchy.length; i++) {
    eligible.push(categoryHierarchy[i]);
  }
  
  return eligible;
}

/**
 * Extract age category from competition category string
 * e.g., "Solo Adult" -> "Adult", "Solo Contemporary Youth" -> "Youth"
 */
export function extractAgeCategoryFromCompetitionCategory(competitionCategory) {
  if (!competitionCategory) return null;
  
  const categories = ['Juvenile II', 'Juvenile I', 'Junior II', 'Junior I', 'Youth', 'Adult'];
  
  for (const category of categories) {
    if (competitionCategory.includes(category)) {
      return category;
    }
  }
  
  return null;
}

/**
 * Batch pre-populate the age-category cache from a single query on approved profileClaims.
 * Call this once before bulk getDancerCurrentAgeCategory lookups to minimise Firestore reads.
 */
export async function batchPrefetchAgeCategoriesFromClaims() {
  // Public users may not have access to profileClaims - that's OK, individual lookups fall back
  const claimYears = await loadClaimBirthYears();
  if (!claimYears) return;
  const currentYear = new Date().getFullYear();
  claimYears.forEach((birthYear, name) => {
    if (!_ageCategoryCache.has(name)) {
      _ageCategoryCache.set(name, categoryFromAge(currentYear - birthYear));
    }
  });
}

/**
 * Get dancer's current age category from their claimed profile
 */
export async function getDancerCurrentAgeCategory(dancerName) {
  // Return from cache if already resolved
  if (_ageCategoryCache.has(dancerName)) {
    return _ageCategoryCache.get(dancerName);
  }

  // Priority: 1. Claimed profile birth date, 2. dancerProfiles dateOfBirth, 3. admin manualYearOfBirth
  const birthYear = await getDancerBirthYear(dancerName);
  const result = birthYear ? categoryFromAge(new Date().getFullYear() - birthYear) : null;
  _ageCategoryCache.set(dancerName, result);
  return result;
}

/**
 * Helper function to parse birth date string and extract year
 * Handles both dd/mm/yyyy and yyyy-mm-dd formats
 */
function parseBirthYear(dateString) {
  if (!dateString) return null;
  
  // If it's a Date object, get the year directly
  if (dateString instanceof Date) {
    return dateString.getFullYear();
  }
  
  // If it's a string, try to parse it
  if (typeof dateString === 'string') {
    // Handle dd/mm/yyyy format
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const year = parseInt(parts[2]);
        if (!isNaN(year)) return year;
      }
    }
    // Handle yyyy-mm-dd format
    else if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        if (!isNaN(year)) return year;
      }
    }
    
    // Try standard Date parsing as fallback
    const parsedDate = new Date(dateString);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.getFullYear();
    }
  }
  
  return null;
}

function categoryFromAge(age) {
  if (age >= 7 && age <= 9) return 'Juvenile I';
  if (age >= 10 && age <= 11) return 'Juvenile II';
  if (age >= 12 && age <= 13) return 'Junior I';
  if (age >= 14 && age <= 15) return 'Junior II';
  if (age >= 16 && age <= 18) return 'Youth';
  if (age >= 19) return 'Adult';
  return null; // Age not in valid range
}

// dancerName -> birth year from approved claims, loaded with ONE query (null if not permitted)
let _claimBirthYearsPromise = null;
function loadClaimBirthYears() {
  if (!_claimBirthYearsPromise) {
    _claimBirthYearsPromise = getDocs(
      query(collection(db, 'profileClaims'), where('status', '==', 'approved'))
    ).then(snap => {
      const map = new Map();
      snap.forEach(d => {
        const data = d.data();
        if (data.dancerName && data.birthDate && !map.has(data.dancerName)) {
          const y = parseBirthYear(data.birthDate);
          if (y) map.set(data.dancerName, y);
        }
      });
      return map;
    }).catch(() => null); // public users may not be allowed to list claims
  }
  return _claimBirthYearsPromise;
}

// dancerName -> Promise<birth year | null>. Birth year is year-independent, so one lookup
// serves every year / category the user switches to.
const _birthYearCache = new Map();

function getDancerBirthYear(dancerName) {
  if (!_birthYearCache.has(dancerName)) {
    _birthYearCache.set(dancerName, resolveBirthYear(dancerName));
  }
  return _birthYearCache.get(dancerName);
}

async function resolveBirthYear(dancerName) {
  try {
    // Priority 1: claimed profile birth date
    const claimYears = await loadClaimBirthYears();
    if (claimYears) {
      if (claimYears.has(dancerName)) return claimYears.get(dancerName);
    } else {
      // Bulk read not permitted - fall back to the per-dancer claims query
      try {
        const claimsSnapshot = await getDocs(query(
          collection(db, 'profileClaims'),
          where('dancerName', '==', dancerName),
          where('status', '==', 'approved')
        ));
        if (!claimsSnapshot.empty) {
          const claimData = claimsSnapshot.docs[0].data();
          const y = claimData.birthDate ? parseBirthYear(claimData.birthDate) : null;
          if (y) return y;
        }
      } catch (claimsError) {
        // fall through to dancerProfiles
      }
    }

    // Priority 2/3: dancerProfiles dateOfBirth, then admin manualYearOfBirth
    const { doc, getDoc } = await import("firebase/firestore");
    const profileSnap = await getDoc(doc(db, 'dancerProfiles', dancerName));
    if (profileSnap.exists()) {
      const profileData = profileSnap.data();
      if (profileData.dateOfBirth) {
        const y = parseBirthYear(profileData.dateOfBirth);
        if (y) return y;
      }
      if (profileData.manualYearOfBirth) return profileData.manualYearOfBirth;
    }
    return null;
  } catch (error) {
    console.error('Error getting dancer birth year:', error);
    return null;
  }
}

/**
 * Get dancer's age category for a specific year from their claimed profile
 */
export async function getDancerAgeCategoryForYear(dancerName, year) {
  const birthYear = await getDancerBirthYear(dancerName);
  return birthYear ? categoryFromAge(year - birthYear) : null;
}

/**
 * Check if a competition result should count for rankings
 * based on dancer's current age category
 */
export function shouldResultCountForRankings(competitionCategory, dancerCurrentCategory) {
  if (!dancerCurrentCategory) {
    // If we don't know the dancer's current category, count all results (backwards compatibility)
    return true;
  }
  
  const resultAgeCategory = extractAgeCategoryFromCompetitionCategory(competitionCategory);
  if (!resultAgeCategory) {
    // If we can't determine the age category from competition, count it (backwards compatibility)
    return true;
  }
  
  const eligibleCategories = getEligibleCategoriesForRankings(dancerCurrentCategory);
  return eligibleCategories.includes(resultAgeCategory);
}

/**
 * Check if a competition result should count for rankings for a specific year
 * based on dancer's age category in that year
 */
export function shouldResultCountForRankingsInYear(competitionCategory, dancerAgeCategoryInYear) {
  if (!dancerAgeCategoryInYear) {
    // If we don't know the dancer's age category for that year, count all results (backwards compatibility)
    return true;
  }
  
  const resultAgeCategory = extractAgeCategoryFromCompetitionCategory(competitionCategory);
  if (!resultAgeCategory) {
    // If we can't determine the age category from competition, count it (backwards compatibility)
    return true;
  }
  
  const eligibleCategories = getEligibleCategoriesForRankings(dancerAgeCategoryInYear);
  return eligibleCategories.includes(resultAgeCategory);
}

/**
 * Check if a dancer competed in a VALID age category at the time of competition
 * 
 * Rules:
 * - A dancer CAN compete in their current age category OR one category HIGHER (dancing up is allowed)
 * - A dancer CANNOT compete in a category LOWER than their actual age category (dancing down is NOT allowed)
 * 
 * Example: A Junior II dancer (age 14-15) can compete in Junior II or Youth, but NOT in Junior I or Juvenile
 * 
 * @param {string} competitionCategory - The category they competed in (e.g., "Solo Youth", "Solo Junior II")
 * @param {string} dancerAgeCategoryAtCompetition - The dancer's actual age category at competition time based on birth date
 * @returns {boolean} - true if dancer competed in a valid category (same or higher), false if they competed in a lower category
 */
export function wasInCorrectCategoryAtCompetition(competitionCategory, dancerAgeCategoryAtCompetition) {
  if (!dancerAgeCategoryAtCompetition) {
    // If we don't know the dancer's age category at competition time, allow (backwards compatibility)
    return true;
  }
  
  const competitionAgeCategory = extractAgeCategoryFromCompetitionCategory(competitionCategory);
  if (!competitionAgeCategory) {
    // If we can't determine the age category from competition, allow (backwards compatibility)
    return true;
  }
  
  // Compare the competition category with the dancer's actual age category
  // Dancer can compete in their category or HIGHER, but NOT lower
  const comparison = compareAgeCategories(competitionAgeCategory, dancerAgeCategoryAtCompetition);
  
  // comparison < 0 means competition category is LOWER than dancer's age (NOT ALLOWED - dancing down)
  // comparison = 0 means same category (ALLOWED)
  // comparison > 0 means competition category is HIGHER than dancer's age (ALLOWED - dancing up)
  return comparison >= 0;
}

/**
 * Check if a dancer changed age category since January 1st of current year
 */
export async function didDancerChangeAgeCategoryThisYear(dancerName) {
  try {
    const currentYear = new Date().getFullYear();
    const currentAgeCategory = await getDancerAgeCategoryForYear(dancerName, currentYear);
    const januaryAgeCategory = await getDancerAgeCategoryForYear(dancerName, currentYear); // Same as current for simplicity
    
    if (!currentAgeCategory) {
      return false;
    }
    
    // Check if dancer has a claimed profile to get birth date
    const claimsQuery = query(
      collection(db, 'profileClaims'),
      where('dancerName', '==', dancerName),
      where('status', '==', 'approved')
    );
    
    const claimsSnapshot = await getDocs(claimsQuery);
    
    if (claimsSnapshot.empty) {
      return false;
    }
    
    const claimData = claimsSnapshot.docs[0].data();
    if (!claimData.birthDate) {
      return false;
    }
    
    // Calculate what their age category was on January 1st vs now
    const january1st = new Date(currentYear, 0, 1); // January 1st of current year
    const now = new Date();
    
    // Parse birth date
    let birthYear;
    if (claimData.birthDate.includes('-')) {
      const parts = claimData.birthDate.split('-');
      birthYear = parseInt(parts[0]);
    } else if (claimData.birthDate.includes('/')) {
      const parts = claimData.birthDate.split('/');
      birthYear = parseInt(parts[2]);
    } else {
      return false;
    }
    
    const ageOnJan1st = january1st.getFullYear() - birthYear;
    const ageNow = now.getFullYear() - birthYear;
    
    // Age category is based on birth year, so both should be the same
    // But we check if they moved from one category to another at the start of the year
    const jan1stCategory = calculateAgeCategoryFromAge(ageOnJan1st);
    const currentCategory = calculateAgeCategoryFromAge(ageNow);
    
    // For 2025: if born in 2006, they were 18 (Youth) at start of year but are now 19 (Adult)
    // Actually, if born in 2006, they are 19 in 2025 regardless of birthday
    // So we need to check if they were in a different category in the previous year
    const prevYear = currentYear - 1;
    const ageLastYear = prevYear - birthYear;
    const lastYearCategory = calculateAgeCategoryFromAge(ageLastYear);
    
    const didChange = lastYearCategory !== currentCategory;
    
    return didChange;
  } catch (error) {
    console.error('Error checking age category change:', error);
    return false;
  }
}

/**
 * Check if dancer changed age category in a specific year
 */
export async function didDancerChangeAgeCategoryInYear(dancerName, year) {
  try {
    // Check if dancer has a claimed profile to get birth date
    const claimsQuery = query(
      collection(db, 'profileClaims'),
      where('dancerName', '==', dancerName),
      where('status', '==', 'approved')
    );
    
    const claimsSnapshot = await getDocs(claimsQuery);
    
    if (claimsSnapshot.empty) {
      return false;
    }
    
    const claimData = claimsSnapshot.docs[0].data();
    if (!claimData.birthDate) {
      return false;
    }
    
    // Parse birth date
    let birthYear;
    if (claimData.birthDate.includes('-')) {
      const parts = claimData.birthDate.split('-');
      birthYear = parseInt(parts[0]);
    } else if (claimData.birthDate.includes('/')) {
      const parts = claimData.birthDate.split('/');
      birthYear = parseInt(parts[2]);
    } else {
      return false;
    }
    
    // Check if they changed category from previous year to this year
    const prevYear = year - 1;
    const ageInPrevYear = prevYear - birthYear;
    const ageInYear = year - birthYear;
    
    const prevYearCategory = calculateAgeCategoryFromAge(ageInPrevYear);
    const yearCategory = calculateAgeCategoryFromAge(ageInYear);
    
    const didChange = prevYearCategory !== yearCategory;
    
    return didChange;
  } catch (error) {
    console.error('Error checking age category change for year:', error);
    return false;
  }
}

/**
 * Calculate age category from age number
 */
export function calculateAgeCategoryFromAge(age) {
  if (age >= 7 && age <= 9) return 'Juvenile I';
  if (age >= 10 && age <= 11) return 'Juvenile II';
  if (age >= 12 && age <= 13) return 'Junior I';
  if (age >= 14 && age <= 15) return 'Junior II';
  if (age >= 16 && age <= 18) return 'Youth';
  if (age >= 19) return 'Adult';
  return null;
}

/**
 * Find the year when a dancer transitioned from one age category to a higher one
 * @param {string} dancerName - The dancer's name
 * @param {string} fromCategory - The lower age category they were in
 * @param {string} toCategory - The higher age category they moved to
 * @returns {number|null} - The year of transition, or null if not found
 */
export async function findAgeCategoryTransitionYear(dancerName, fromCategory, toCategory) {
  try {
    // Get the dancer's birth year
    let birthYear = null;
    
    // Check claimed profile first (highest priority)
    const claimsQuery = query(
      collection(db, 'profileClaims'),
      where('dancerName', '==', dancerName),
      where('status', '==', 'approved')
    );
    
    const claimsSnapshot = await getDocs(claimsQuery);
    
    if (!claimsSnapshot.empty) {
      const claimData = claimsSnapshot.docs[0].data();
      if (claimData.birthDate) {
        if (claimData.birthDate.includes('-')) {
          birthYear = parseInt(claimData.birthDate.split('-')[0]);
        } else if (claimData.birthDate.includes('/')) {
          birthYear = parseInt(claimData.birthDate.split('/')[2]);
        }
      }
    } else {
      // Check for admin manual year of birth
      const { doc, getDoc } = await import("firebase/firestore");
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const profileData = profileSnap.data();
        birthYear = profileData.manualYearOfBirth;
      }
    }
    
    if (!birthYear) return null;
    
    // Calculate transition year based on age category boundaries
    // Youth: 16-18 (transition at age 19 to Adult)
    // Junior II: 14-15 (transition at age 16 to Youth)
    // Junior I: 12-13 (transition at age 14 to Junior II)
    // Juvenile II: 10-11 (transition at age 12 to Junior I)
    // Juvenile I: 7-9 (transition at age 10 to Juvenile II)
    
    const transitionAges = {
      'Juvenile I': 10,   // Age when leaving Juvenile I
      'Juvenile II': 12,  // Age when leaving Juvenile II
      'Junior I': 14,     // Age when leaving Junior I
      'Junior II': 16,    // Age when leaving Junior II
      'Youth': 19         // Age when leaving Youth
    };
    
    const transitionAge = transitionAges[fromCategory];
    if (!transitionAge) return null;
    
    // The transition happens on January 1st of the year they reach the transition age
    const transitionYear = birthYear + transitionAge;
    
    return transitionYear;
  } catch (error) {
    console.error('Error finding age category transition year:', error);
    return null;
  }
}

/**
 * Check if a result should be automatically excluded due to age category change
 * 
 * Requirements:
 * - If dancer's current age category > result age category: auto-exclude from Star Ranking, Current Rank, Current Days
 * - Best Rank/Best Days from lower categories: only count until Dec 31 of result year
 * - Auto-exclusion overrides manual exclusion
 * - Invalid results excluded from EVERYTHING
 */
export async function shouldAutoExcludeResult(dancerName, competitionDate, competitionCategory) {
  try {
    const compDate = competitionDate instanceof Date ? competitionDate : new Date(competitionDate);
    const compYear = compDate.getFullYear();
    
    // Get the competition's age category
    const resultAgeCategory = extractAgeCategoryFromCompetitionCategory(competitionCategory);
    if (!resultAgeCategory) return { 
      shouldExclude: false, 
      isHistorical: false,
      excludeFromBestMetrics: false,
      competitionYear: compYear 
    };
    
    // Get dancer's age category at the time of the competition (not current)
    const competitionAgeCategory = await getDancerAgeCategoryForYear(dancerName, compYear);
    
    if (!competitionAgeCategory) return { 
      shouldExclude: false, 
      isHistorical: false,
      excludeFromBestMetrics: false,
      competitionYear: compYear 
    };
    
    // Also get current age category for comparison and transition year calculation
    const currentYear = new Date().getFullYear();
    const currentAgeCategory = await getDancerAgeCategoryForYear(dancerName, currentYear);
    
    // 🎯 CRITICAL FIX: Compare competition age category with result age category
    // Only auto-exclude if the dancer should have been in a HIGHER category during that competition
    const categoryComparison = compareAgeCategories(competitionAgeCategory, resultAgeCategory);
    
    // If dancer was in correct category or lower category during competition, no exclusions needed
    if (categoryComparison <= 0) {
      return { 
        shouldExclude: false, 
        isHistorical: false,
        excludeFromBestMetrics: false,
        competitionYear: compYear 
      };
    }
    
    // Dancer should have been in higher age category during the competition
    // This means they were competing in wrong (too low) category - exclude from active rankings
    const shouldExclude = true;
    
    // Check if 365 days have passed since competition (for historical marking)
    const currentDate = new Date();
    const daysSinceCompetition = Math.floor((currentDate - compDate) / (1000 * 60 * 60 * 24));
    const isHistorical = daysSinceCompetition > 365;
    
    // 🎯 December 31st cutoff logic: Find the year when dancer transitioned to higher category
    // Get the age category they should have been in during the competition year
    const dancerCategoryInCompYear = await getDancerAgeCategoryForYear(dancerName, compYear);
    
    let excludeFromBestMetrics = false;
    
    if (dancerCategoryInCompYear && compareAgeCategories(dancerCategoryInCompYear, resultAgeCategory) > 0) {
      // If dancer should have been in higher category during competition year,
      // apply December 31st cutoff of the competition year
      const endOfCompetitionYear = new Date(compYear, 11, 31, 23, 59, 59);
      excludeFromBestMetrics = currentDate > endOfCompetitionYear;
    } else {
      // Find the exact year when dancer transitioned from result category to higher category
      const transitionYear = await findAgeCategoryTransitionYear(dancerName, resultAgeCategory, currentAgeCategory);
      
      if (transitionYear) {
        // Apply December 31st cutoff of the year BEFORE transition
        // (results in old category valid until Dec 31 of the year before moving up)
        const cutoffYear = Math.max(compYear, transitionYear - 1);
        const cutoffDate = new Date(cutoffYear, 11, 31, 23, 59, 59);
        excludeFromBestMetrics = currentDate > cutoffDate;
      } else {
        // Fallback: use December 31st of competition year
        const endOfCompetitionYear = new Date(compYear, 11, 31, 23, 59, 59);
        excludeFromBestMetrics = currentDate > endOfCompetitionYear;
      }
    }
    
    console.log(`� December 31st cutoff check for ${dancerName}: ${resultAgeCategory} → ${currentAgeCategory}`);
    console.log(`   Competition: ${compDate.toDateString()} (${compYear})`);
    console.log(`   Exclude from rankings: ${shouldExclude}, Historical: ${isHistorical}, Best metrics cutoff: ${excludeFromBestMetrics}`);
    
    return { 
      shouldExclude, 
      isHistorical, 
      excludeFromBestMetrics,
      competitionYear: compYear 
    };
  } catch (error) {
    console.error('Error checking auto-exclusion:', error);
    return { 
      shouldExclude: false, 
      isHistorical: false,
      excludeFromBestMetrics: false,
      competitionYear: new Date().getFullYear()
    };
  }
}
